import { useEffect, useMemo, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import AppShell from '@/components/AppShell';
import { Button } from '@/components/ui';
import Icon from '@/components/Icon';
import ReservationTeaser from '@/components/ReservationTeaser';
import SharePanel from '@/components/SharePanel';
import { getMe, cachedMe, getGuestTrips, ApiError } from '@/lib/api';
import {
  addGuestToken,
  listGuestTokens,
  removeGuestToken,
  useGuest,
} from '@/lib/guest';
import { resolveTripPhotos } from '@/lib/trip-hero';
import { shuffle } from '@/lib/shuffle';
import { loadAllReservations, summarizeBookToday, type BookTodaySummary } from '@/lib/all-reservations';
import type { TripListItem } from '@/lib/types';
import {
  deriveHomeTrips,
  tripState,
  tripStatus,
  fmtRange,
  startTime,
  homePalette,
  earliestYear,
} from '@/lib/home-trips';
import type { HomeTrip, Status } from '@/lib/home-trips';
import { plural } from '@/lib/humanize';

// Trips homepage (design_handoff_homepage): photo-led library classified by
// date into a featured "Next up" hero + Upcoming + Past (desaturated) + a
// Drafts lane. Per-trip color from the dcg band; real hero photos (deduped by
// city, best-effort) with attribution. Replaces the old flat Active/Drafts grid.

type Swatch = { c1: string; c2: string };
type Photo = { url: string; credit?: string };

function toneStyle(tone: string) {
  return (
    {
      live: { dot: '#FCFAF6', bg: 'var(--accent)', fg: '#FCFAF6', bd: 'var(--accent)' },
      soon: { dot: 'var(--accent)', bg: 'var(--glass, rgba(252,250,246,0.92))', fg: 'var(--accent-deep)', bd: 'transparent' },
      up: { dot: 'var(--sage)', bg: 'var(--glass, rgba(252,250,246,0.92))', fg: 'var(--ink)', bd: 'transparent' },
      past: { dot: 'var(--ink-mute)', bg: 'var(--glass, rgba(252,250,246,0.85))', fg: 'var(--ink-soft)', bd: 'transparent' },
    } as Record<string, { dot: string; bg: string; fg: string; bd: string }>
  )[tone] || { dot: 'var(--ink-mute)', bg: 'var(--glass, rgba(252,250,246,0.9))', fg: 'var(--ink-soft)', bd: 'transparent' };
}

function StatusPill({ status }: { status: Status }) {
  const t = toneStyle(status.tone);
  return (
    <span
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: 'var(--r-pill)',
        background: t.bg, color: t.fg, border: `1px solid ${t.bd}`, backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
        fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '0.04em', textTransform: 'uppercase', whiteSpace: 'nowrap',
      }}
    >
      <span style={{ width: 5, height: 5, borderRadius: '50%', background: t.dot, animation: status.tone === 'live' ? 'wa-pulse-ring 2s infinite' : 'none' }} />
      {status.label}
    </span>
  );
}

// Cheap deterministic string hash — gives each card a stable per-trip
// slideshow phase so the grid doesn't all cross-fade on the same tick.
function hashStr(s: string): number {
  let h = 0;
  for (let k = 0; k < s.length; k++) h = (Math.imul(h, 31) + s.charCodeAt(k)) | 0;
  return Math.abs(h);
}

// Cross-fading slideshow over a trip's photo pool (like mobile's marquee).
// Advances every ~5s; a single photo shows statically; an empty pool falls back
// to the accent gradient. `seed` (the trip id) phase-shifts each card's cycle so
// the cards on the grid don't cross-fade in lockstep — staggering them reads as
// alive rather than jarring.
function useSlideIndex(count: number, seed?: string): number {
  const [i, setI] = useState(0);
  useEffect(() => {
    setI(0);
    if (count <= 1) return;
    const period = 5200;
    const phase = seed ? hashStr(seed) % period : 0;
    let interval: ReturnType<typeof setInterval> | undefined;
    // First advance after a full period + the card's phase, then steady cadence.
    const kickoff = setTimeout(() => {
      setI((x) => (x + 1) % count);
      interval = setInterval(() => setI((x) => (x + 1) % count), period);
    }, period + phase);
    return () => {
      clearTimeout(kickoff);
      if (interval !== undefined) clearInterval(interval);
    };
  }, [count, seed]);
  return i;
}

function SlideMedia({
  photos, idx, c, past, hover,
}: { photos?: Photo[]; idx: number; c: Swatch; past?: boolean; hover?: boolean }) {
  const has = !!photos && photos.length > 0;
  return (
    <div style={{ position: 'absolute', inset: 0 }}>
      {!has && <div style={{ position: 'absolute', inset: 0, background: `linear-gradient(150deg, ${c.c1}, ${c.c2})` }} />}
      {has && photos!.map((p, k) => (
        <div
          key={p.url}
          style={{
            position: 'absolute', inset: 0,
            backgroundImage: `url(${p.url})`, backgroundSize: 'cover', backgroundPosition: 'center',
            filter: past ? 'saturate(0.55) brightness(0.96)' : 'none',
            opacity: k === idx ? 1 : 0,
            transform: hover ? 'scale(1.04)' : 'scale(1)',
            transition: 'opacity 1.1s ease, transform var(--dur-slow) var(--ease)',
          }}
        />
      ))}
    </div>
  );
}

function Card({ trip, c, photos, now, onOpen }: { trip: HomeTrip; c: Swatch; photos?: Photo[]; now: Date; onOpen: () => void }) {
  const [hover, setHover] = useState(false);
  const past = tripState(trip, now) === 'past';
  const idx = useSlideIndex(photos?.length ?? 0, trip.id);
  const cur = photos?.[idx];
  return (
    <button
      onClick={onOpen}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'block', width: '100%', textAlign: 'left', padding: 0, border: '1px solid var(--line)', borderRadius: 'var(--r-md)',
        overflow: 'hidden', background: 'var(--surface)', cursor: 'pointer', fontFamily: 'var(--font-ui)',
        boxShadow: hover ? 'var(--shadow-lifted)' : 'var(--shadow-card)', transform: hover ? 'translateY(-3px)' : 'none',
        transition: 'box-shadow var(--dur), transform var(--dur)',
      }}
    >
      <div style={{ position: 'relative', height: 150, overflow: 'hidden' }}>
        <SlideMedia photos={photos} idx={idx} c={c} past={past} hover={hover} />
        <div style={{ position: 'absolute', inset: 0, background: `linear-gradient(180deg, rgba(20,16,12,0.34) 0%, transparent 36%, transparent 58%, rgba(20,16,12,0.42) 100%), linear-gradient(150deg, ${c.c1}44, transparent 60%)` }} />
        <div style={{ position: 'absolute', top: 10, left: 10 }}><StatusPill status={tripStatus(trip, now)} /></div>
        <div style={{ position: 'absolute', bottom: 9, left: 12, right: 12 }}>
          <div className="wa-mono" style={{ fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(252,250,246,0.82)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{trip.eyebrow}</div>
          <div className="wa-display" style={{ fontSize: 21, lineHeight: 1.04, color: '#FCFAF6', textShadow: '0 1px 6px rgba(0,0,0,0.35)', marginTop: 2, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{trip.name}</div>
        </div>
        {cur?.credit && <span className="wa-mono" style={{ position: 'absolute', top: 10, right: 10, maxWidth: '40%', padding: '2px 6px', borderRadius: 4, background: 'rgba(20,16,12,0.5)', fontSize: 11, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'rgba(252,250,246,0.92)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{cur.credit}</span>}
      </div>
      <div style={{ padding: '11px 13px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <span className="wa-mono" style={{ fontSize: 11, color: 'var(--ink-soft)' }}>{fmtRange(trip)}</span>
        <span style={{ width: 9, height: 9, borderRadius: 3, background: `linear-gradient(135deg, ${c.c1}, ${c.c2})`, flexShrink: 0, filter: past ? 'saturate(0.6)' : 'none' }} />
      </div>
      <div style={{ padding: '0 13px 12px' }}>
        <span className="wa-mono" style={{ fontSize: 11, color: 'var(--ink-mute)', letterSpacing: '0.03em' }}>
          {plural(trip.nights, 'night')} · {plural(trip.stops, 'stop')}
        </span>
      </div>
    </button>
  );
}

function Hero({ trip, c, photos, now, navigate, guest }: { trip: HomeTrip; c: Swatch; photos?: Photo[]; now: Date; navigate: (to: string) => void; guest?: boolean }) {
  const idx = useSlideIndex(photos?.length ?? 0, trip.id);
  const cur = photos?.[idx];
  return (
    <div className="stack-phone" style={{ display: 'grid', gridTemplateColumns: '1fr 1.15fr', borderRadius: 'var(--r-lg)', overflow: 'hidden', border: '1px solid var(--line)', boxShadow: 'var(--shadow-card)', minHeight: 320, background: 'var(--surface)' }}>
      <div style={{ position: 'relative', padding: '36px 38px', display: 'flex', flexDirection: 'column' }}>
        <div style={{ position: 'absolute', inset: 0, background: `linear-gradient(150deg, ${c.c1}26, transparent 62%)`, pointerEvents: 'none' }} />
        <div style={{ position: 'relative', flex: 1, display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span className="wa-eyebrow" style={{ whiteSpace: 'nowrap' }}>Next up</span>
            <span style={{ width: 5, height: 5, borderRadius: 3, background: 'var(--ink-mute)', flexShrink: 0 }} />
            <span className="wa-mono" style={{ fontSize: 11, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--accent-deep)', whiteSpace: 'nowrap' }}>{tripStatus(trip, now).label}</span>
          </div>
          <h2 className="wa-display" style={{ fontSize: 46, lineHeight: 0.98, margin: '14px 0 0', maxWidth: 460 }}>{trip.name}</h2>
          <div className="wa-mono" style={{ fontSize: 12, color: 'var(--ink-soft)', marginTop: 14, letterSpacing: '0.03em', textTransform: 'uppercase' }}>{trip.eyebrow}</div>
          <div style={{ flex: 1 }} />
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 26, marginTop: 24, flexWrap: 'wrap' }}>
            {([['Dates', fmtRange(trip)], ['Nights', String(trip.nights)], ['Stops', String(trip.stops)]] as Array<[string, string]>).map(([k, v], i) => (
              <div key={i}>
                <div className="wa-eyebrow" style={{ marginBottom: 4 }}>{k}</div>
                <div style={{ fontFamily: i === 0 ? 'var(--font-ui)' : 'var(--font-display)', fontSize: i === 0 ? 14 : 26, fontWeight: i === 0 ? 500 : 400, color: 'var(--ink)' }}>{v}</div>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 26 }}>
            {guest ? (
              <Button variant="ink" size="lg" iconR="arrowR" onClick={() => navigate(`/trip/${trip.id}`)}>Open the trip</Button>
            ) : (
              <>
                <Button variant="ink" size="lg" iconR="arrowR" onClick={() => navigate(`/trip/${trip.id}/planner`)}>Open the planner</Button>
                <Button variant="secondary" size="lg" icon="home" onClick={() => navigate(`/trip/${trip.id}`)}>Overview</Button>
              </>
            )}
          </div>
        </div>
      </div>
      <div style={{ position: 'relative' }}>
        <SlideMedia photos={photos} idx={idx} c={c} />
        <div style={{ position: 'absolute', inset: 0, background: `linear-gradient(110deg, var(--surface) 0%, transparent 22%), linear-gradient(150deg, ${c.c1}30, transparent 55%)` }} />
        {cur?.credit && <span className="wa-mono" style={{ position: 'absolute', bottom: 8, right: 10, maxWidth: '85%', padding: '2px 6px', borderRadius: 4, background: 'rgba(20,16,12,0.5)', fontSize: 11, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'rgba(252,250,246,0.92)', display: 'inline-flex', alignItems: 'center', gap: 4 }}><Icon name="camera" size={11} /> {cur.credit}</span>}
      </div>
    </div>
  );
}

function Section({ label, count, children }: { label: string; count: string; children: ReactNode }) {
  return (
    <section style={{ marginTop: 44 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 16 }}>
        <h3 className="wa-display" style={{ fontSize: 26, whiteSpace: 'nowrap', flexShrink: 0 }}>{label}</h3>
        <span className="wa-mono" style={{ fontSize: 11, color: 'var(--ink-mute)', whiteSpace: 'nowrap' }}>{count}</span>
      </div>
      {children}
    </section>
  );
}

const GRID: CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(244px, 1fr))', gap: 18 };

export default function TripsList() {
  const navigate = useNavigate();
  const { isGuest } = useGuest();
  const now = useMemo(() => new Date(), []);
  const [view, setView] = useState<
    | { kind: 'loading' }
    | { kind: 'ready'; trips: HomeTrip[]; raw: TripListItem[] }
    | { kind: 'error'; message: string }
  >(() => {
    // Warm-start from the cached /api/me so the trips render instantly; the load
    // effect below refreshes them.
    const cached = cachedMe();
    return cached?.trips?.length
      ? { kind: 'ready', trips: deriveHomeTrips(cached.trips), raw: cached.trips }
      : { kind: 'loading' };
  });
  const [photos, setPhotos] = useState<Record<string, Photo[]>>({});
  const [resSummary, setResSummary] = useState<BookTodaySummary | null>(null);

  // Global reservation tracker summary — fans out over upcoming/active trips'
  // reservations (lib/all-reservations.ts), best-effort, independent of the
  // trip list render. Feeds the ReservationTeaser CTA. Member-only: it
  // reads /api/me, and /reservations is a member surface anyway.
  useEffect(() => {
    if (isGuest) return;
    let cancelled = false;
    loadAllReservations()
      .then((list) => {
        if (!cancelled) setResSummary(summarizeBookToday(list));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [isGuest]);

  useEffect(() => {
    let cancelled = false;
    if (isGuest) {
      // Anonymous share-link follower: same homepage, fed from the
      // stored grant(s) instead of /api/me. Dead tokens (revoked links)
      // are pruned so they stop shadowing the SignInScreen.
      (async () => {
        const byId = new Map<string, TripListItem>();
        let liveTokens = 0;
        for (const token of listGuestTokens()) {
          try {
            const res = await getGuestTrips(token);
            liveTokens += 1;
            addGuestToken(token, res.trips.map((t) => t.tripId));
            for (const t of res.trips) {
              if (!byId.has(t.tripId)) byId.set(t.tripId, t);
            }
          } catch (err) {
            if (
              err instanceof ApiError &&
              [401, 403, 404].includes(err.status)
            ) {
              removeGuestToken(token);
            }
          }
        }
        if (cancelled) return;
        if (liveTokens === 0) {
          setView({
            kind: 'error',
            message:
              'This share link is no longer active. Ask the trip owner for a fresh one.',
          });
          return;
        }
        const raw = [...byId.values()];
        setView({ kind: 'ready', trips: deriveHomeTrips(raw), raw });
      })();
      return () => {
        cancelled = true;
      };
    }
    getMe()
      .then((res) => {
        if (!cancelled) setView({ kind: 'ready', trips: deriveHomeTrips(res.trips), raw: res.trips });
      })
      .catch((err) => {
        if (cancelled) return;
        const message =
          err instanceof ApiError && err.status === 401
            ? 'Not signed in. Refresh after authenticating with Cloudflare Access.'
            : err instanceof Error
              ? err.message
              : 'Failed to load trips.';
        setView({ kind: 'error', message });
      });
    return () => {
      cancelled = true;
    };
  }, [isGuest]);

  // hero photos — resolved per trip through the SAME pipeline as mobile
  // (curated display photos → quality-gated /api/photos/hero keyed by destId,
  // not the bare city label). See lib/trip-hero.ts. Keyed by trip id; filled in
  // as they arrive. A null result leaves the card on its accent gradient,
  // exactly as mobile renders an ambiguous custom pin like Dunsmuir.
  useEffect(() => {
    if (view.kind !== 'ready') return;
    let cancelled = false;
    view.raw.forEach((trip) => {
      resolveTripPhotos(trip)
        .then((pool) => {
          if (cancelled || !pool.length) return;
          // Shuffled so the card/hero slideshow opens on a random photo each load.
          setPhotos((prev) => ({ ...prev, [trip.tripId]: shuffle(pool) }));
        })
        .catch(() => {});
    });
    return () => {
      cancelled = true;
    };
  }, [view]);

  if (view.kind === 'loading') {
    return <AppShell><p className="loading" style={{ color: 'var(--ink-mute)' }}>Loading trips…</p></AppShell>;
  }
  if (view.kind === 'error') {
    return <AppShell><p className="error" style={{ color: 'var(--accent-deep)' }}>{view.message}</p></AppShell>;
  }

  const trips = view.trips;
  const palette = homePalette(trips);
  const swatch = (t: HomeTrip): Swatch => palette[t.city] ?? { c1: '#b6a48f', c2: '#8c7a64' };

  const upcoming = trips.filter((t) => { const s = tripState(t, now); return s === 'upcoming' || s === 'active'; }).sort((a, b) => startTime(a) - startTime(b));
  const past = trips.filter((t) => tripState(t, now) === 'past').sort((a, b) => startTime(b) - startTime(a));
  const drafts = trips.filter((t) => tripState(t, now) === 'draft');
  const hero = upcoming[0];
  const restUpcoming = upcoming.slice(1);
  const totalNights = trips.reduce((s, t) => s + t.nights, 0);
  const sinceYear = earliestYear(trips);

  const dateLine = now.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });

  return (
    <AppShell>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 20, marginBottom: 26 }}>
        <div>
          <div className="wa-eyebrow">{dateLine}</div>
          <h1 className="wa-display" style={{ fontSize: 40, margin: '8px 0 0' }}>Trips</h1>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 10 }}>
          <div className="wa-mono" style={{ fontSize: 11, color: 'var(--ink-mute)', textAlign: 'right', lineHeight: 1.7 }}>
            {plural(trips.length, 'trip')} · {plural(totalNights, 'night')}{sinceYear ? <><br />since {sinceYear}</> : null}
          </div>
          {view.raw.some((t) => t.role === 'owner') && (
            <SharePanel trips={view.raw} />
          )}
          {isGuest && (
            <span
              className="wa-mono"
              title="You're following these trips through a share link — read-only"
              style={{
                fontSize: 11,
                letterSpacing: '0.08em',
                padding: '4px 9px',
                borderRadius: 99,
                border: '1px solid var(--line)',
                background: 'var(--surface-2)',
                color: 'var(--ink-mute)',
              }}
            >
              GUEST VIEW
            </span>
          )}
        </div>
      </div>

      {hero && <Hero trip={hero} c={swatch(hero)} photos={photos[hero.id]} now={now} navigate={navigate} guest={isGuest} />}

      <ReservationTeaser summary={resSummary} onOpen={() => navigate('/reservations')} />

      {restUpcoming.length > 0 && (
        <Section label="Upcoming" count={`${restUpcoming.length} planned`}>
          <div style={GRID}>
            {restUpcoming.map((t) => <Card key={t.id} trip={t} c={swatch(t)} photos={photos[t.id]} now={now} onOpen={() => navigate(`/trip/${t.id}`)} />)}
          </div>
        </Section>
      )}

      {drafts.length > 0 && (
        <Section label="Drafts" count={`${drafts.length}`}>
          <div style={GRID}>
            {drafts.map((t) => <Card key={t.id} trip={t} c={swatch(t)} photos={photos[t.id]} now={now} onOpen={() => navigate(`/trip/${t.id}`)} />)}
          </div>
        </Section>
      )}

      {past.length > 0 && (
        <Section label="Past trips" count={`${past.length} · a life in trips`}>
          <div style={GRID}>
            {past.map((t) => <Card key={t.id} trip={t} c={swatch(t)} photos={photos[t.id]} now={now} onOpen={() => navigate(`/trip/${t.id}`)} />)}
          </div>
        </Section>
      )}

      {trips.length === 0 && (
        <div className="empty-state" style={{ marginTop: 40 }}>
          {isGuest ? (
            <>
              <h2>Nothing shared yet</h2>
              <p>The trips on your share link will show up here.</p>
            </>
          ) : (
            <>
              <h2>No trips yet</h2>
              <p><a href="/new">Create your first trip →</a></p>
            </>
          )}
        </div>
      )}
    </AppShell>
  );
}
